/** 三方搜索 API 单条结果 */
export type SearchResultRow = Record<string, string | number | null | undefined>;

export type ParsedSearchApiResponse = {
  ok: boolean;
  rows: SearchResultRow[];
  totalRows: number;
  message?: string;
};

const PRIORITY_FIELD_KEYS = ['序号', '姓名', '身份证', '手机号', '查询关键词'];

export const SEARCH_RESULT_PAGE_SIZE = 10;

/**
 * 解析 API1 响应：status=200 时返回完整 data 数组。
 */
export function parseSearchApiResponse(responseJson?: string): ParsedSearchApiResponse {
  if (!responseJson?.trim()) {
    return { ok: false, rows: [], totalRows: 0, message: '无响应数据' };
  }
  try {
    const parsed = JSON.parse(responseJson) as {
      status?: number;
      data?: SearchResultRow[];
      total_rows?: number;
      message?: string;
      msg?: string;
    };
    if (parsed.status !== 200) {
      return {
        ok: false,
        rows: [],
        totalRows: 0,
        message: parsed.message || parsed.msg || `查询失败（status=${parsed.status ?? '未知'}）`,
      };
    }
    const rows = Array.isArray(parsed.data) ? parsed.data : [];
    const totalRows = parsed.total_rows ?? rows.length;
    if (rows.length === 0) {
      return {
        ok: true,
        rows: [],
        totalRows,
        message: '未查询到匹配数据',
      };
    }
    return {
      ok: true,
      rows,
      totalRows,
    };
  } catch {
    const trimmed = responseJson.trim();
    if (trimmed.length > 0 && trimmed.length < 500 && !trimmed.startsWith('{') && !trimmed.startsWith('[')) {
      return { ok: false, rows: [], totalRows: 0, message: trimmed };
    }
    return { ok: false, rows: [], totalRows: 0, message: '响应解析失败' };
  }
}

/** 安全展示：null/undefined/'' → '—' */
export function formatSearchFieldValue(v: unknown): string {
  if (v == null || v === '') {
    return '—';
  }
  return String(v);
}

/** 合并多行字段名（去重）；常用字段优先，其余按首次出现顺序 */
export function collectSearchResultFieldKeys(rows: SearchResultRow[]): string[] {
  const seen = new Set<string>();
  const ordered: string[] = [];

  for (const key of PRIORITY_FIELD_KEYS) {
    if (rows.some((row) => Object.prototype.hasOwnProperty.call(row, key))) {
      seen.add(key);
      ordered.push(key);
    }
  }

  for (const row of rows) {
    for (const key of Object.keys(row)) {
      if (!seen.has(key)) {
        seen.add(key);
        ordered.push(key);
      }
    }
  }

  return ordered;
}

/** 前端分页切片（page 从 1 开始） */
export function paginateSearchRows(
  rows: SearchResultRow[],
  page: number,
  pageSize: number,
): SearchResultRow[] {
  const safePage = Math.max(1, page);
  const start = (safePage - 1) * pageSize;
  return rows.slice(start, start + pageSize);
}

export function getSearchResultPageCount(totalRows: number, pageSize: number): number {
  if (totalRows <= 0) {
    return 0;
  }
  return Math.ceil(totalRows / pageSize);
}
